import { of, from } from 'rxjs';
import { map, mergeMap, startWith, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { authToken, teladocApi } from '@td/api';

import {
  APPLY_FILTER,
  ADD_MANY_INCENTIVES,
  COMPLETE_ADD_MANY_INCENTIVES,
  PICK_INCENTIVE_COST,
  ADD_PROVIDER_TO_INCENTIVE,
  REMOVE_PROVIDER_FROM_INCENTIVE,
  ADD_PROVIDER_TO_INCENTIVE_SUCCESS,
  REMOVE_PROVIDER_FROM_INCENTIVE_SUCCESS,
  DELETE_INCENTIVE,
  FINISH_DELETE_INCENTIVE,
  fetchIncentiveCosts,
  completeRequest,
  fetchIncentiveCostsStart,
  finishFetchIncentiveCostsProviders,
  addProviderToIncentiveSuccess,
  startAddProviderToIncentive,
  removeProviderFromIncentiveSuccess,
  startRemoveProviderFromIncentive,
  completeAddManyIncentives,
  startAddManyIncentivesRequest,
  finishAddManyIncentives,
  finishDeleteIncentive,
  flashMessage,
  startToggleProviderInIncentive
} from './actions';

const makeDeleteRequest = payload => {
  const { url, token } = payload;
  return from(teladocApi.delete(url, token));
};

const makeRequest = payload => {
  const { url, params, token } = payload;
  return from(teladocApi.get(url, params, token));
};

const makePostRequest = payload => {
  const { url, params, token } = payload;
  return from(teladocApi.post(url, params, token));
};


export const deleteIncentiveCostRequest = action$ =>
  action$.pipe(
    ofType(DELETE_INCENTIVE),
    mergeMap(action => {
      const { payload } = action;
      const failureMessage = 'Failed to delete incentive, something went wrong.';
      return makeDeleteRequest(payload).pipe(
        map(_response => finishDeleteIncentive()),
        catchError(_error => of(flashMessage(failureMessage))));
    }));

export const finishManyIncentivesRequests = (action$, _store) =>
  action$.pipe(
    ofType(COMPLETE_ADD_MANY_INCENTIVES),
    mergeMap(action => {
      if (action.response.data.error_record) {
        return [
          flashMessage('Error creating Incentive')
        ];
      } else {
        return [
          finishAddManyIncentives(),
          flashMessage('Created incentives')
        ];
      }
    }));

export const createManyIncentivesRequests = (action$, _store) =>
  action$.pipe(
    ofType(ADD_MANY_INCENTIVES),
    mergeMap(action => {
      const { payloads } = action;
      const failureMessage = 'Failed to create incentives, something went wrong.';
      return makePostRequest(payloads[0]).pipe(
        map(response => completeAddManyIncentives(response)),
        startWith(startAddManyIncentivesRequest()),
        catchError(_error => {
          if (_error.response.status === 400) {
            return of(completeAddManyIncentives(_error.response));
          } else {
            return of(flashMessage(failureMessage));
          }
        }));
    }));


export const toggleProviderInclusionInIncentive = (action$, _store) =>
  action$.pipe(
    ofType(REMOVE_PROVIDER_FROM_INCENTIVE, ADD_PROVIDER_TO_INCENTIVE),
    mergeMap(action => {
      const { incentiveCostId, providerId, payload, type } = action;
      switch (type) {
        case ADD_PROVIDER_TO_INCENTIVE: {
          const failureMessage = 'Failed to add provider to incentive, something went wrong.';
          return makePostRequest(payload).pipe(
            map(response => addProviderToIncentiveSuccess(response, incentiveCostId, providerId)),
            startWith(startToggleProviderInIncentive()),
            catchError(_error => {
              console.error(_error);
              return of(flashMessage(failureMessage));
            }));
        }
        case REMOVE_PROVIDER_FROM_INCENTIVE: {
          const failureMessage = 'Failed to remove provider to incentive, something went wrong.';
          return makeDeleteRequest(payload).pipe(
            map(response =>
              removeProviderFromIncentiveSuccess(response, incentiveCostId, providerId)
            ),
            startWith(startToggleProviderInIncentive()),
            catchError(_error => of(flashMessage(failureMessage))));
        }
        default:
          return null;
      }
    }));

// DEPRECATED
export const removeProviderFromIncentiveRequest = (action$, _store) =>
  action$.pipe(
    ofType(REMOVE_PROVIDER_FROM_INCENTIVE),
    mergeMap(action => {
      const { payload } = action;
      const failureMessage = 'Failed to remove provider from incentive, something went wrong.';
      return makeDeleteRequest(payload).pipe(
        map(response => removeProviderFromIncentiveSuccess(response)),
        startWith(startRemoveProviderFromIncentive()),
        catchError(_error => of(flashMessage(failureMessage))));
    }));

// DEPRECATED
export const addProviderToIncentiveRequest = (action$, _store) =>
  action$.pipe(
    ofType(ADD_PROVIDER_TO_INCENTIVE),
    mergeMap(action => {
      const { payload } = action;
      const failureMessage = 'Failed to add provider to incentive, something went wrong.';
      return makePostRequest(payload).pipe(
        map(response => addProviderToIncentiveSuccess(response)),
        startWith(startAddProviderToIncentive()),
        catchError(_error => of(flashMessage(failureMessage))));
    }));

export const fetchProvidersForIncentiveCostRequest = (action$, _store) =>
  action$.pipe(
    ofType(
      PICK_INCENTIVE_COST,
    ),
    mergeMap(action => {
      let providerIncentiveCostId = 0;
      if (action.type === ADD_PROVIDER_TO_INCENTIVE_SUCCESS) {
        const relation = action.response.data.provider_incentive_relation;
        providerIncentiveCostId = relation.provider_incentive_cost.provider_incentive_cost_id;
      } else if (action.type === REMOVE_PROVIDER_FROM_INCENTIVE_SUCCESS) {
        const incentiveCost = action.response.data.provider_incentive_cost;
        providerIncentiveCostId = incentiveCost.provider_incentive_cost_id;
      } else {
        providerIncentiveCostId = action.incentiveCost.provider_incentive_cost_id;
      }
      const url = `/v4/provider_incentive_costs/${providerIncentiveCostId}/providers?per_page=200`;
      const failureMessage = 'Failed to fetch providers, something went wrong.';
      return makeRequest({ url, token: authToken.get() }).pipe(
        map(response => finishFetchIncentiveCostsProviders(response)),
        catchError(_error => of(flashMessage(failureMessage))));
    }));

export const filterIncentiveCosts = (action$, state$) =>
  action$.pipe(
    ofType(
      APPLY_FILTER,
      FINISH_DELETE_INCENTIVE),
    mergeMap(action => {
      const currentFilter = state$.value.providerIncentiveCost.requestFilter;
      const newFilter = { ...currentFilter, ...action.filter };
      const { payload } = fetchIncentiveCosts(newFilter);
      const failureMessage = 'Failed to filter incentives, something went wrong.';
      return makeRequest(payload).pipe(
        map(response => completeRequest(response)),
        startWith(fetchIncentiveCostsStart()),
        catchError(_error => of(flashMessage(failureMessage))));
    }));
